import React, { useState, useEffect } from 'react';
import './App.css';
import Calendar from './components/Calendar';
import ElementsList from './components/ElementsList';
import { loadFromLocalStorage, saveToLocalStorage } from './utils/storage';

const App = () => {
  // État pour gérer les onglets
  const [activeTab, setActiveTab] = useState('calendar');
  
  // État pour gérer les éléments
  const [elements, setElements] = useState([]);
  
  // État pour gérer les éléments2
  const [elements2, setElements2] = useState([]);
  
  // État pour gérer les associations d'éléments aux dates
  const [dateAssociations, setDateAssociations] = useState({});
  
  // État pour la langue (français par défaut)
  const [language, setLanguage] = useState('fr');
  
  // État pour gérer les noms des onglets
  const [tabNames, setTabNames] = useState({
    elements1: 'Éléments',
    elements2: 'Éléments 2'
  });
  
  // Textes pour l'interface bilingue
  const translations = {
    fr: {
      calendar: 'Calendrier',
      elements: 'Éléments',
      switchLanguage: 'Switch to English',
      exportData: 'Exporter les données',
      importData: 'Importer des données'
    },
    en: {
      calendar: 'Calendar',
      elements: 'Elements',
      switchLanguage: 'Passer en Français',
      exportData: 'Export data',
      importData: 'Import data'
    }
  };
  
  // Chargement des données depuis le stockage local au démarrage
  useEffect(() => {
    const savedData = loadFromLocalStorage();
    if (savedData) {
      if (savedData.elements) setElements(savedData.elements);
      if (savedData.elements2) setElements2(savedData.elements2);
      if (savedData.dateAssociations) setDateAssociations(savedData.dateAssociations);
      if (savedData.language) setLanguage(savedData.language);
      if (savedData.tabNames) setTabNames(savedData.tabNames);
    }
  }, []);
  
  // Sauvegarde des données lorsqu'elles changent
  useEffect(() => {
    saveToLocalStorage({
      elements,
      elements2,
      dateAssociations,
      language,
      tabNames
    });
  }, [elements, dateAssociations, language, elements2, tabNames]);
  
  // Fonction pour basculer la langue
  const toggleLanguage = () => {
    setLanguage(language === 'fr' ? 'en' : 'fr');
  };
  
  // Fonction pour exporter les données
  const exportData = () => {
    const data = JSON.stringify({
      elements,
      elements2,
      dateAssociations
    });
    
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = 'calendar-data.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  // Fonction pour importer les données
  const importData = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result);
          
          if (importedData.elements) {
            setElements(importedData.elements);
          }
          
          if (importedData.elements2) {
            setElements2(importedData.elements2);
          }
          
          if (importedData.dateAssociations) {
            setDateAssociations(importedData.dateAssociations);
          }
          
          // Réinitialiser l'input file
          event.target.value = null;
        } catch (error) {
          console.error("Erreur lors de l'importation des données:", error);
          alert("Format de fichier invalide!");
        }
      };
      
      reader.readAsText(file);
    }
  };

  const handleTabNameEdit = (tabKey, newName) => {
    setTabNames(prev => ({
      ...prev,
      [tabKey]: newName
    }));
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>Gestionnaire de Calendrier</h1>
        <div className="toolbar">
          <button onClick={toggleLanguage}>
            {translations[language].switchLanguage}
          </button>
          <button onClick={exportData}>
            {translations[language].exportData}
          </button>
          <div className="import-container">
            <label htmlFor="import-input" className="import-button">
              {translations[language].importData}
            </label>
            <input
              id="import-input"
              type="file"
              accept=".json"
              onChange={importData}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      </header>
      
      <div className="tabs">
        <button 
          className={activeTab === 'calendar' ? 'active' : ''}
          onClick={() => setActiveTab('calendar')}
        >
          {translations[language].calendar}
        </button>
        <div className="tab-with-edit">
          <button 
            className={activeTab === 'elements' ? 'active' : ''}
            onClick={() => setActiveTab('elements')}
          >
            {tabNames.elements1}
          </button>
          <button 
            className="edit-tab-name"
            onClick={() => {
              const newName = prompt('Nouveau nom pour l\'onglet:', tabNames.elements1);
              if (newName) handleTabNameEdit('elements1', newName);
            }}
          >
            ✏️
          </button>
        </div>
        <div className="tab-with-edit">
          <button 
            className={activeTab === 'elements2' ? 'active' : ''}
            onClick={() => setActiveTab('elements2')}
          >
            {tabNames.elements2}
          </button>
          <button 
            className="edit-tab-name"
            onClick={() => {
              const newName = prompt('Nouveau nom pour l\'onglet:', tabNames.elements2);
              if (newName) handleTabNameEdit('elements2', newName);
            }}
          >
            ✏️
          </button>
        </div>
      </div>
      
      <div className="content">
        {activeTab === 'calendar' ? (
          <Calendar 
            elements={elements}
            elements2={elements2} // Pass elements2 to Calendar
            dateAssociations={dateAssociations}
            setDateAssociations={setDateAssociations}
            language={language}
            translations={translations}
            tabNames={tabNames}
          />
        ) : activeTab === 'elements' ? (
          <ElementsList 
            elements={elements}
            setElements={setElements}
            language={language}
            translations={translations}
          />
        ) : (
          <ElementsList 
            elements={elements2}
            setElements={setElements2} // Pass elements2 to ElementsList
            language={language}
            translations={translations}
          />
        )}
      </div>
    </div>
  );
};

export default App;